import {useState, useCallback, useEffect, useMemo} from 'react';
import get from 'lodash/get';
import produce from 'immer';
import {push} from 'connected-react-router';
import {useTranslation} from 'react-i18next';
import {useSMCSnackbar} from '../../../utils/hooks';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import { useSMCErrorHandler } from '../../../utils/errorUtils';
import store from '../../../store';
import {SMCPaths} from '../../../conf/Routes';
import generalIcon from '../../../assets/images/general.png';
import vpnSettingsIcon from '../../../assets/images/vpn_settings.png';
import pageIcon from '../../../assets/images/page.png';
import historyIcon from '../../../assets/images/history.png';
import CommunityGeneral from './InnerPages/CommunityGeneral';
import CommunityVPNSettings from './InnerPages/CommunityVPNSettings';
import CommunityIKESettings from './InnerPages/CommunityIKESettings';
import CommunityHistory from './InnerPages/CommunityHistory';
import CommunityCustomFields from './InnerPages/CommunityCustomFields';
import { CommunityGetSchema } from '../../../api/DBSchema/Community';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useCommunityEditor(communityId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origCommunityObject, setOrigCommunityObject] = useState({});
    const [communityObject, setCommunityObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

        // Does community have custom field page in editor
    const hasCustomFieldPage = useMemo(() => {
        return !!get(communityObject, [CommunityGetSchema.customFieldOne]);
    }, [communityObject]);

    const editorPages = useMemo(() => ([
            { id: 'general', title: t('SMP.GENERAL'), icon: generalIcon, pageElement: CommunityGeneral, helpId: helpContextPageUrl.communityGeneral },
            { id: 'vpnSettings', title: t('SMP.COMMUNITIES.TABLE.INNER_MENU.VPN_SETTINGS'), icon: vpnSettingsIcon, pageElement: CommunityVPNSettings, helpId: helpContextPageUrl.communityVPNSettings },
            { id: 'ikeSettings', title: t('SMP.COMMUNITIES.TABLE.INNER_MENU.IKE_SETTINGS'), icon: pageIcon, pageElement: CommunityIKESettings, helpId: helpContextPageUrl.communityIKESettings },
            { id: 'customFields', title: t('SMC_SETTINGS_CUSTOM'), icon: pageIcon, pageElement: CommunityCustomFields, hide: !hasCustomFieldPage, helpId: helpContextPageUrl.communityCustomFields },
            { id: 'history', title: t('SMP.COMMUNITIES.TABLE.INNER_MENU.HISTORY'), icon: historyIcon, pageElement: CommunityHistory, helpId: helpContextPageUrl.communityHistory }
        ]
    ), [hasCustomFieldPage, t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchCommunity = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchCommunityResp = await Api.fetchSingleObject(ObjectTypes.COMMUNITY, communityId);
            const community = fetchCommunityResp.data.data.community;
            // Validate server response
            if (!community) {
                console.error('Get community response failed: community response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigCommunityObject(community);
            setCommunityObject(community);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [communityId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setCommunityData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setCommunityObject(communityObj => produce(communityObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.COMMUNITY, communityId, communityObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [communityId, getMessagesArrayByPages, communityObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.COMMUNITY, communityId, communityObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchCommunity();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [getErrorMessage, communityId, fetchCommunity, communityObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [communityId] ))) {
            Api.deleteObjects(ObjectTypes.COMMUNITY, [communityId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.communities));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [communityId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [communityId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setCommunityObject(origCommunityObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origCommunityObject]);

    // Fetch community on loading of page
    useEffect(() => {
        fetchCommunity();
    }, [fetchCommunity]);
    
    return {
        communityObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setCommunityData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchCommunity
    };
}